import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, Save, Link2, MousePointerClick } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
});

const networks = [
  "Check24",
  "Verivox",
  "Awin",
  "CJ Affiliate",
  "Telekom Partner",
  "Vodafone Partner",
  "O2 Partner",
  "Diğer"
];

export default function AdminAffiliateLinks() {
  const [links, setLinks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    network: "",
    category_id: "",
    is_active: true,
  });

  const fetchData = async () => {
    try {
      const [linksRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/affiliate-links`, { headers: getAuthHeaders() }),
        axios.get(`${API}/categories`),
      ]);
      setLinks(linksRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        category_id: formData.category_id || null,
      };
      
      if (editingLink) {
        await axios.put(`${API}/affiliate-links/${editingLink.id}`, submitData, {
          headers: getAuthHeaders(),
        });
        toast.success("Affiliate link güncellendi");
      } else {
        await axios.post(`${API}/affiliate-links`, submitData, {
          headers: getAuthHeaders(),
        });
        toast.success("Affiliate link eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu affiliate linki silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/affiliate-links/${id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Affiliate link silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const handleEdit = (link) => {
    setEditingLink(link);
    setFormData({
      name: link.name,
      url: link.url,
      network: link.network,
      category_id: link.category_id || "",
      is_active: link.is_active,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingLink(null);
    setFormData({
      name: "",
      url: "",
      network: "",
      category_id: "",
      is_active: true,
    });
  };

  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || "Genel";

  return (
    <AdminLayout title="Affiliate Linkler">
      <div className="space-y-6" data-testid="admin-affiliate-links">
        <div className="flex justify-between items-center">
          <p className="text-slate-600">Affiliate ağlarından gelen linkleri yönetin.</p>
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-electric-500 hover:bg-electric-600"
            data-testid="add-affiliate-link-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni Link
          </Button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-slate-100 rounded-lg animate-pulse"></div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {links.map((link) => (
              <Card key={link.id} className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Link2 className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-navy-900">{link.name}</h3>
                        <p className="text-sm text-slate-500">
                          {link.network} • {getCategoryName(link.category_id)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2 text-sm">
                        <MousePointerClick className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{link.clicks || 0}</span>
                        <span className="text-slate-500">tıklama</span>
                      </div>
                      
                      <span className={`px-2 py-1 text-xs rounded-full ${link.is_active ? "bg-signal-100 text-signal-700" : "bg-slate-100 text-slate-500"}`}>
                        {link.is_active ? "Aktif" : "Pasif"}
                      </span>
                      
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(link)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(link.id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs text-slate-500 truncate">{link.url}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {links.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                Henüz affiliate link eklenmemiş.
              </div>
            )}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingLink ? "Affiliate Link Düzenle" : "Yeni Affiliate Link"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Link Adı *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Vattenfall Strom - Check24"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Affiliate URL *</Label>
                <Input
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://..."
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ağ (Network) *</Label>
                  <Select 
                    value={formData.network} 
                    onValueChange={(v) => setFormData({ ...formData, network: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Ağ seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {networks.map((network) => (
                        <SelectItem key={network} value={network}>{network}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Kategori</Label>
                  <Select 
                    value={formData.category_id} 
                    onValueChange={(v) => setFormData({ ...formData, category_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Kategori seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Genel</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label>Aktif</Label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  İptal
                </Button>
                <Button type="submit" className="bg-electric-500 hover:bg-electric-600">
                  <Save className="w-4 h-4 mr-2" />
                  Kaydet
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
